import { z } from 'zod/v4';

/* ─────────────────── Aggregated schema-arrays imported from each namespace ─────────────────── */
import {
  authenticationNotificationEventSchemas,
  authenticationRequestEventSchemas,
  authenticationResponseEventSchemas,
} from './authentication';
/* Passthrough namespaces */
import {
  eip1193NotificationPassthroughSchema,
  eip1193RequestPassthroughSchema,
  eip1193ResponsePassthroughSchema,
} from './eip1193';
import {
  handshakeNotificationEventSchemas,
  handshakeRequestEventSchemas,
  handshakeResponseEventSchemas,
} from './handshake';
import {
  heartbeatNotificationEventSchemas,
  heartbeatRequestEventSchemas,
  heartbeatResponseEventSchemas,
} from './heartbeat';
import {
  infinexOpsNotificationEventSchemas,
  infinexOpsRequestEventSchemas,
  infinexOpsResponseEventSchemas,
} from './infinexOps';
import {
  infinexUiNotificationEventSchemas,
  infinexUiRequestEventSchemas,
  infinexUiResponseEventSchemas,
} from './infinexUi';
/* Tests */
import {
  popupInfoNotificationEventSchemas,
  popupInfoRequestEventSchemas,
  popupInfoResponseEventSchemas,
} from './popupInfo';
import { serverResponseNotificationEventSchemas } from './serverResponse';
import {
  sessionChangeNotificationEventSchemas,
  sessionChangeRequestEventSchemas,
  sessionChangeResponseEventSchemas,
} from './sessionChange';
import {
  solanaWalletNotificationPassthroughSchema,
  solanaWalletRequestPassthroughSchema,
  solanaWalletResponsePassthroughSchema,
} from './solanaWallet';
import {
  storedDataRequestEventSchemas,
  storedDataResponseEventSchemas,
} from './storedData';

/* ──────────────────────────────── Request Schemas ──────────────────────────────── */
const eventRequestSchemas = [
  ...handshakeRequestEventSchemas,
  ...heartbeatRequestEventSchemas,
  ...infinexOpsRequestEventSchemas,
  ...authenticationRequestEventSchemas,
  ...sessionChangeRequestEventSchemas,
  ...storedDataRequestEventSchemas,
  ...infinexUiRequestEventSchemas,
  ...popupInfoRequestEventSchemas,
] as const;

/* ─────────────────────────────── Response Schemas ─────────────────────────────── */
const eventResponseSchemas = [
  ...handshakeResponseEventSchemas,
  ...heartbeatResponseEventSchemas,
  ...infinexOpsResponseEventSchemas,
  ...authenticationResponseEventSchemas,
  ...sessionChangeResponseEventSchemas,
  ...storedDataResponseEventSchemas,
  ...infinexUiResponseEventSchemas,
  ...popupInfoResponseEventSchemas,
] as const;

/* ──────────────────────────── Notification Schemas ────────────────────────────── */
const eventNotificationSchemas = [
  ...handshakeNotificationEventSchemas,
  ...authenticationNotificationEventSchemas,
  ...sessionChangeNotificationEventSchemas,
  ...infinexUiNotificationEventSchemas,
  ...serverResponseNotificationEventSchemas,
  ...heartbeatNotificationEventSchemas,
  ...infinexOpsNotificationEventSchemas,
  ...popupInfoNotificationEventSchemas,
] as const;

/* ──────────────────────────── Build discriminated unions ─────────────────────────── */
export const infinexRequestEventSchema = z.discriminatedUnion('type', [
  z.discriminatedUnion('method', eventRequestSchemas),
  eip1193RequestPassthroughSchema,
  solanaWalletRequestPassthroughSchema,
]);

export const infinexResponseEventSchema = z.discriminatedUnion('type', [
  z.discriminatedUnion('method', eventResponseSchemas),
  eip1193ResponsePassthroughSchema,
  solanaWalletResponsePassthroughSchema,
]);

export const infinexNotificationEventSchema = z.discriminatedUnion('type', [
  z.discriminatedUnion('method', eventNotificationSchemas),
  eip1193NotificationPassthroughSchema,
  solanaWalletNotificationPassthroughSchema,
]);

export const infinexEventSchema = z.union([
  infinexRequestEventSchema,
  infinexResponseEventSchema,
  infinexNotificationEventSchema,
]);

/**
 * ─────────────────────────── Exported TypeScript types ───────────────────────────
 */
export type InfinexRequestEvent = z.infer<typeof infinexRequestEventSchema>;
export type InfinexResponseEvent = z.infer<typeof infinexResponseEventSchema>;
export type InfinexNotificationEvent = z.infer<
  typeof infinexNotificationEventSchema
>;
export type InfinexEvent = z.infer<typeof infinexEventSchema>;
